import { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { MARKETPLACE_CURRENCY_DENOMINATION } from "../../../../utils/marketplaceConstants";
import { useTabIndex } from "react-tabindex";
import { Switch } from "../../../fields/base/Switch";
import type { Column } from "react-table-6";
import type { EditColumnOptions } from "../../../../modules/merchandising/components/columns/buildEditColumn";
import { errorToast } from "../../../../utils/toasts";

const toComparableNumber = (value: unknown) => {
  const parsed = parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
};

const formatCellValue = (value: string, format?: string) => {
  if (format === "currency") {
    return parseFloat(value).toFixed(2);
  }

  if (format === "percent") {
    return parseFloat(value).toFixed(1);
  }

  return parseFloat(value).toFixed(1);
};

interface Props {
  row: Record<string, unknown> & {
    _original: Record<string, unknown> & {
      handleUpdate: (row: unknown, key: string, value: number | string | undefined) => void;
    };
  };
  value: string;
  column: Column;
  options: EditColumnOptions;
  marketPlace?: {
    marketPlace: string;
  };
}

const EditCell = ({ row, value, column, options, marketPlace }: Props) => {
  const { t } = useTranslation(["merchandising"]);
  const formattedDefault = formatCellValue(value, options.format);

  const [editValue, setEditValue] = useState(formattedDefault);
  const [selected, setSelected] = useState(false);

  const tabIndex = useTabIndex(options.tabIndex);

  const { validations, format } = options;

  useEffect(() => {
    setEditValue(formatCellValue(value, format));
    setSelected(false);

    // oxlint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  const validate = (value: number | string, key: string) => {
    const { minValue, maxValue, blank, compare } = validations ?? {};

    const label = typeof column.Header === "string" ? column.Header : t(($) => $.merchandising["Value"]);
    const currencyPrefix =
      format === "currency" ? MARKETPLACE_CURRENCY_DENOMINATION[marketPlace?.marketPlace ?? "USA"] : "";
    const percentSuffix = format === "percent" ? "%" : "";

    if (Number(value) < Number(minValue)) {
      errorToast({
        message: t(($) => $.merchandising["Invalid value. {label} must be at least {constraint}."], {
          label,
          constraint: `${currencyPrefix}${minValue}${percentSuffix}`,
        }),
      });
      setEditValue(formatCellValue(row._original[key] as string, format));
      return false;
    }
    if (Number(value) > Number(maxValue)) {
      errorToast({
        message: t(($) => $.merchandising["Invalid value. {label} must be less than {constraint}."], {
          label,
          constraint: `${currencyPrefix}${maxValue}${percentSuffix}`,
        }),
      });

      setEditValue(formatCellValue(row._original[key] as string, format));
      return false;
    }
    if (!value && !blank) {
      errorToast({
        message: t(($) => $.merchandising["Invalid value. {label} is required."], { label }),
      });
      setEditValue(formatCellValue(row._original[key] as string, format));
      return false;
    }
    if (compare) {
      const editedValue = toComparableNumber(value);
      const siblingValue = toComparableNumber(row._original[compare.key]);
      const invalid =
        editedValue !== null &&
        siblingValue !== null &&
        (compare.operator === "lessThan" ? editedValue >= siblingValue : editedValue <= siblingValue);

      if (invalid) {
        errorToast({ message: compare.message });
        setEditValue(formatCellValue(row._original[key] as string, format));
        return false;
      }
    }

    return true;
  };

  let hidden;

  if (typeof options.renderPlaceholderCellContent === "function") {
    hidden = options.renderPlaceholderCellContent(row);
  }

  if (hidden) {
    return <div className="d-flex align-items-center justify-content-end h-100">-</div>;
  }

  const isDisabled = options.disabled || options.getCellDisabled?.(row) || false;

  if (format === "toggle") {
    return (
      <div className="h-100 w-100 d-flex align-items-center justify-content-end">
        <Switch
          isSelected={row._original[column.id!] === options.onValue}
          isDisabled={isDisabled}
          onChange={() => {
            const { onValue, offValue } = options;

            const updateValue = row._original[column.id!] === onValue ? offValue : onValue;

            if (options.toggleAction && typeof options.toggleAction === "function") {
              options.toggleAction(row._original, updateValue);

              return;
            }

            row._original.handleUpdate(row, column.id!, updateValue);
          }}
        />
      </div>
    );
  }

  return (
    <div className="d-flex align-items-center h-100 ps-3">
      <span>{format === "currency" && MARKETPLACE_CURRENCY_DENOMINATION[marketPlace?.marketPlace ?? "USA"]}</span>
      <div className="h-100 d-flex align-items-center justify-content-end">
        <span
          style={{
            position: "absolute",
            marginRight: "-15px",
            marginTop: "-2px",
          }}
        >
          {format === "percent" ? "%" : ""}
        </span>

        <input
          type="number"
          className={`text-end ${options.strikeThroughText ? options.strikeThroughText(row) : ""}`}
          value={editValue}
          onChange={(e) => {
            setEditValue(e.target.value);
          }}
          onClick={() => {
            setSelected(true);
          }}
          tabIndex={tabIndex}
          onBlur={() => {
            const key = column.id!;
            setSelected(false);

            const validInput = validate(editValue, key);
            if (!validInput || row._original[key] === parseFloat(editValue)) {
              return;
            }

            row._original.handleUpdate(row, key, Number(editValue));
          }}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.currentTarget.blur();
            }
          }}
          disabled={isDisabled}
          style={{
            width: "100%",
            border: "none",
            borderBottom: selected ? "1px solid #555" : "1px solid #EEF",
            backgroundColor: "transparent",
            paddingLeft: "25px",
          }}
        />
      </div>
    </div>
  );
};

export default EditCell;
